import React, { useEffect, useState } from 'react';

function InstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    });
    window.addEventListener('appinstalled', () => setInstalled(true));
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setInstalled(true);
      setDeferredPrompt(null);
    }
  };

  if (installed) return (
    <div style={{ textAlign: 'center', color: 'var(--green)', fontWeight: 700, letterSpacing: 1 }}>
      ✓ App Installed!
    </div>
  );

  if (deferredPrompt) return (
    <button className="btn btn-secondary" onClick={handleInstall}>
      📲 Install UmpBuddy
    </button>
  );

  return (
    <div style={{
      background: 'var(--surface)',
      border: '2px solid var(--border)',
      borderRadius: 'var(--radius)',
      padding: '14px 16px',
      fontSize: 13,
      color: 'var(--gray)',
      lineHeight: 1.7,
      textAlign: 'center'
    }}>
      📲 <strong style={{ color: 'var(--white)' }}>Install on iPhone:</strong> Tap <strong style={{ color: 'var(--white)' }}>Share</strong> → <strong style={{ color: 'var(--white)' }}>"Add to Home Screen"</strong><br />
      📲 <strong style={{ color: 'var(--white)' }}>Install on Android:</strong> Tap the menu → <strong style={{ color: 'var(--white)' }}>"Add to Home Screen"</strong>
    </div>
  );
}

export default function Landing({ navigate }) {
  return (
    <div className="screen" style={{ background: 'var(--bg)' }}>

      {/* Hero */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 24px 32px', textAlign: 'center', gap: 8 }}>

        {/* Logo */}
        <svg width="130" height="130" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"
          style={{ filter: 'drop-shadow(0 0 24px rgba(58,170,82,0.5))', marginBottom: 12 }}>
          <polygon points="100,8 192,100 100,192 8,100" fill="#1a3d20"/>
          <polygon points="100,22 178,100 100,178 22,100" fill="none" stroke="#3aaa52" strokeWidth="3"/>
          <path d="M74,58 Q68,82 68,98 L68,116 Q70,128 100,130 Q130,128 132,116 L132,98 Q132,82 126,58 Q114,48 100,46 Q86,48 74,58Z" fill="#3aaa52"/>
          <rect x="52" y="70" width="18" height="38" rx="5" fill="#3aaa52"/>
          <rect x="130" y="70" width="18" height="38" rx="5" fill="#3aaa52"/>
          <rect x="76" y="76" width="48" height="18" rx="3" fill="#1a3d20"/>
          <rect x="68" y="116" width="64" height="8" rx="3" fill="#3aaa52"/>
          <path d="M68,124 Q68,148 80,162 Q90,172 100,174 Q110,172 120,162 Q132,148 132,124Z" fill="#1a3d20" stroke="#3aaa52" strokeWidth="3.5"/>
          <line x1="70" y1="132" x2="130" y2="132" stroke="#3aaa52" strokeWidth="3"/>
          <line x1="72" y1="142" x2="128" y2="142" stroke="#3aaa52" strokeWidth="3"/>
          <line x1="75" y1="152" x2="125" y2="152" stroke="#3aaa52" strokeWidth="3"/>
          <line x1="80" y1="162" x2="120" y2="162" stroke="#3aaa52" strokeWidth="2.5"/>
          <line x1="88" y1="124" x2="84" y2="168" stroke="#3aaa52" strokeWidth="2.5"/>
          <line x1="100" y1="124" x2="100" y2="172" stroke="#3aaa52" strokeWidth="2.5"/>
          <line x1="112" y1="124" x2="116" y2="168" stroke="#3aaa52" strokeWidth="2.5"/>
        </svg>

        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 56, letterSpacing: 6, color: 'var(--white)', lineHeight: 1 }}>UMP</div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 56, letterSpacing: 6, color: 'var(--green)', lineHeight: 1 }}>BUDDY</div>
        <div style={{ fontSize: 12, letterSpacing: 3, color: 'var(--gray)', textTransform: 'uppercase', marginTop: 4 }}>Real-Time Game Tracker</div>
      </div>

      {/* Tagline */}
      <div style={{ padding: '0 24px 24px', textAlign: 'center' }}>
        <p style={{ fontSize: 17, color: 'var(--white)', lineHeight: 1.7, fontWeight: 500 }}>
          No scoreboard? No problem.<br />
          <span style={{ color: 'var(--green)' }}>Everyone follows along live</span> — just share a code.
        </p>
      </div>

      {/* CTA Buttons */}
      <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <button className="btn btn-primary btn-lg" onClick={() => navigate('app')}>
          ⚾ Open the App
        </button>
        <button className="btn btn-secondary" onClick={() => navigate('howto')}>
          📖 How It Works
        </button>
        <InstallButton />
      </div>

      {/* Feature highlights */}
      <div style={{ padding: '32px 24px 8px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[
            { icon: '📡', title: 'Live Updates', desc: 'Scores update in real time for everyone' },
            { icon: '🔑', title: 'Game Code', desc: 'Join any game instantly — no account needed' },
            { icon: '⚙️', title: 'Fully Custom', desc: 'Set your own innings, mercy rules & more' },
            { icon: '📱', title: 'Installable', desc: 'Add to your home screen and launch it just like any other app.' },
          ].map(f => (
            <div key={f.title} style={{
              background: 'var(--surface)',
              border: '2px solid var(--border)',
              borderRadius: 'var(--radius)',
              padding: '16px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6
            }}>
              <div style={{ fontSize: 24 }}>{f.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--white)' }}>{f.title}</div>
              <div style={{ fontSize: 12, color: 'var(--gray)', lineHeight: 1.5 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '32px 24px 16px', fontSize: 12, color: 'var(--gray)' }}>
        © 2025 UmpBuddy · <span style={{ color: 'var(--green)', cursor: 'pointer' }} onClick={() => navigate('contact')}>Contact Us</span>
      </div>

    </div>
  );
}
